import { useState } from "react";
import { Badge, DataTable, EmptyState, ErrorState, TrialBanner } from "@/components/DataTable";
import { FilterBar, PageHead, SelectField, TextField } from "@/components/Filters";
import { inr } from "@/lib/api";
import { saveSearch, useFilterOptions, useModuleData } from "./hooks";

const INIT = { counselling_body: "", year: "", college: "", branch: "", min_fee: "", max_fee: "" };

export default function Fees() {
  const opts = useFilterOptions();
  const [f, setF] = useState(INIT);
  const [sort, setSort] = useState({ by: "total_fee", dir: 1 });
  const { rows, total, locked, visible, loading, error } = useModuleData("/fees", {
    ...f,
    sort_by: sort.by,
    sort_dir: sort.dir,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const columns = [
    {
      key: "college",
      label: "College",
      minWidth: 340,
      render: (r) => (
        <span className="block max-w-[420px] whitespace-normal" title={r.college}>
          {r.college}
        </span>
      ),
    },
    { key: "state", label: "State", width: 110, render: (r) => <span className="text-slate-600">{r.state || "NA"}</span> },
    { key: "branch", label: "Branch", minWidth: 170 },
    { key: "quota", label: "Quota", width: 130, render: (r) => (r.quota ? <Badge tone="blue" width="quota">{r.quota}</Badge> : <span className="text-slate-400">NA</span>) },
    { key: "tuition_fee", label: "Tuition", align: "right", width: 120, mono: true, render: (r) => inr(r.tuition_fee) },
    { key: "stipend", label: "Stipend", align: "right", width: 110, mono: true, render: (r) => inr(r.stipend) },
    { key: "total_fee", label: "Total / year", align: "right", width: 140, highlight: true, render: (r) => inr(r.total_fee) },
    { key: "year", label: "Year", width: 70, muted: true },
    { key: "counselling_body", label: "Body", width: 80, muted: true },
  ];

  return (
    <div className="space-y-4" data-testid="fees-page">
      <PageHead
        title="Fee Structure"
        subtitle="Year-specific fee records. Fees are always stored against the year they were published for."
        right={
          <span className="font-mono-num text-sm text-slate-500" data-testid="fees-count">
            {total.toLocaleString("en-IN")} records
          </span>
        }
      />
      <FilterBar onReset={() => setF(INIT)} onSave={() => saveSearch("fees", f)}>
        <SelectField label="Body" testId="fee-filter-body" value={f.counselling_body} onChange={set("counselling_body")} options={opts.counselling_bodies} />
        <SelectField label="Year" testId="fee-filter-year" value={f.year} onChange={set("year")} options={opts.years} />
        <TextField label="College" testId="fee-filter-college" value={f.college} onChange={set("college")} placeholder="Search college" />
        <TextField label="Branch" testId="fee-filter-branch" value={f.branch} onChange={set("branch")} placeholder="Search branch" />
        <TextField label="Min fee" testId="fee-filter-min" type="number" value={f.min_fee} onChange={set("min_fee")} placeholder="0" />
        <TextField label="Max fee" testId="fee-filter-max" type="number" value={f.max_fee} onChange={set("max_fee")} placeholder="2500000" />
      </FilterBar>

      {error && <ErrorState message={error} />}
      {locked && <TrialBanner total={total} visible={visible} />}

      {loading ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          testId="fees-empty"
          title="No fee records found"
          hint="Fee data is published per year through the admin ingestion pipeline. Try widening the fee range."
        />
      ) : (
        <DataTable
          testId="fees-table"
          columns={columns}
          rows={rows}
          sort={sort}
          onSort={(key) => setSort((s) => ({ by: key, dir: s.by === key ? -s.dir : 1 }))}
        />
      )}
    </div>
  );
}
